import React, { useState, useMemo } from "react";
import {
  Table, TableBody, TableHead,
  Toolbar, Typography, CircularProgress, Dialog,
  DialogTitle, DialogContent, DialogActions, Button, TextField, Chip,
  Box, FormControl, InputLabel, Select, MenuItem, InputAdornment
} from "@mui/material";
import { useDebounce } from "../hooks/useDebounce";
import { useCursorList, toCursorPage } from "../hooks/useCursorList";
import LoadMoreBar from "../components/common/LoadMoreBar";
import PageHelp from "../components/common/PageHelp";
import SearchIcon from '@mui/icons-material/Search';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import InfoIcon from '@mui/icons-material/Info';
import FilterListIcon from '@mui/icons-material/FilterList';

import { getRides, getRide } from "../services/rideService";
import { Ride } from "../types/Ride";
import { useNavigate } from "react-router-dom";
import {
  StyledTableContainer,
  StyledTableCell,
  StyledTableRow,
  StyledPaper,
} from "../assets/styles/tableStyle";

import Tooltip from "@mui/material/Tooltip";
import { PaymentStatus, RideStatus } from "../types/enums";
import FancyContextMenu, { ContextMenuAction } from "../components/FancyContextMenu";

const columns = [
  { label: "ID", accessor: "id" },
  { label: "Rider Phone", accessor: "riderId" },
  { label: "Driver Phone", accessor: "driverId" },
  { label: "Pickup", accessor: "pickupLocation" },
  { label: "Dropoff", accessor: "dropoffLocation" },
  { label: "Status", accessor: "status" },
  { label: "Payment Status", accessor: "paymentStatus" },
  { label: "Type", accessor: "type" },
  { label: "Fare", accessor: "fare" },
  { label: "Requested", accessor: "requestedAt" },
  { label: "Matched", accessor: "matchedAt" },
  { label: "Started", accessor: "startedAt" },
  { label: "Completed", accessor: "completedAt" },
  { label: "Cancelled", accessor: "cancelledAt" },
  { label: "Cancelled By", accessor: "cancelledBy" },
  { label: "Cancel Reason", accessor: "cancellationReason" },
  { label: "Distance (m)", accessor: "distanceMeters" },
  { label: "Duration (s)", accessor: "durationSeconds" },
  { label: "Tip", accessor: "tip" },
  { label: "Subsidy (Burn)", accessor: "riderSubsidyApplied" },
  { label: "Total Fare", accessor: "totalFare" }
];

const PAGE_SIZE = 10;

const statusColor = (status: RideStatus | string) => {
  switch (status) {
    case 'requested': return 'default';
    case 'accepted': return 'info';
    case 'in_progress': return 'warning';
    case 'completed': return 'success';
    case 'cancelled': return 'error';
    case 'expired': return 'default';
    default: return 'default';
  }
};

const paymentStatusColor = (status: PaymentStatus | string) => {
  switch (status) {
    case 'paid': return 'success';
    case 'pending': return 'warning';
    case 'failed': return 'error';
    case 'refunded': return 'info';
    default: return 'default';
  }
};

function useQueryParams() {
  const search = window.location.search;
  const params = new URLSearchParams(search);
  const driverUid = params.get('driverUid');
  const riderUid = params.get('riderUid');
  return { driverUid, riderUid };
}

const formatDate = (date?: string | null) =>
    date ? new Date(date).toLocaleString() : "-";

const RideList: React.FC = () => {
  const navigate = useNavigate();

  // Filter & Search State
  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearch = useDebounce(searchTerm);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [paymentFilter, setPaymentFilter] = useState<string>("all");

  // Modal & Context Menu State
  const [detailOpen, setDetailOpen] = useState<boolean>(false);
  const [selectedRide, setSelectedRide] = useState<Ride | null>(null);
  const [contextMenu, setContextMenu] = useState<{ top: number; left: number } | null>(null);
  const [menuRideId, setMenuRideId] = useState<number | null>(null);

  const { driverUid, riderUid } = useQueryParams();

  // Server-side, cursor-paginated, debounced search (scales to millions).
  const ridesList = useCursorList<Ride>({
    key: ['admin-rides', { driverUid, riderUid, debouncedSearch, statusFilter }],
    pageSize: PAGE_SIZE,
    fetchPage: async ({ cursor, limit }) => {
      const res = await getRides({
        cursor,
        limit,
        driverUid: driverUid || undefined,
        riderUid: riderUid || undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        search: debouncedSearch || undefined,
      });
      return toCursorPage<Ride>(res.data);
    },
  });

  const loading = ridesList.isLoading || ridesList.isFetching;
  const error = ridesList.isError ? "Unable to fetch rides." : "";

  // Client-side payment filter (not handled by backend)
  const filteredItems: Ride[] = useMemo(() => {
    if (paymentFilter === "all") return ridesList.rows;
    return ridesList.rows.filter((ride: Ride) => (ride.paymentStatus as any) === paymentFilter);
  }, [ridesList.rows, paymentFilter]);

  const handleRowClick = async (id: number) => {
    setDetailOpen(true);
    const res = await getRide(id);
    setSelectedRide(res.data);
  };

  const handleContextMenu = (event: React.MouseEvent, id: number) => {
    event.preventDefault();
    setMenuRideId(id);
    setContextMenu({ top: event.clientY, left: event.clientX });
  };

  const handleCloseDetail = () => {
    setDetailOpen(false);
    setSelectedRide(null);
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
    setMenuRideId(null);
  };

  const menuActions: ContextMenuAction[] = [
    {
      dividerBefore: true,
      label: "View Full Details",
      icon: <InfoIcon fontSize="small" />,
      onClick: () => {
        if (menuRideId) navigate(`/ride/${menuRideId}`);
      },
      color: "info"
    },
    {
      label: "Check Support Tickets",
      icon: <SupportAgentIcon fontSize="small" />,
      onClick: () => {
        if (menuRideId) navigate(`/support-tickets?rideId=${menuRideId}`);
      },
      color: "warning" // or warning/success/error
    }
  ];

  return (
      <StyledPaper sx={{ maxWidth: "98vw", width: "100%", margin: '0 auto', my: 2, display: 'flex', flexDirection: 'column', height: 'calc(100vh - 120px)', overflow: 'hidden' }}>
        <Toolbar sx={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, flexShrink: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="h6">Ride Management</Typography>
            <PageHelp k="rides" />
          </Box>

          {/* Controls Container */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            {/* Search Input */}
            <TextField
                placeholder="Search ID, Location..."
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" color="action" />
                      </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 200 }}
            />

            {/* Status Filter */}
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Ride Status</InputLabel>
              <Select
                  value={statusFilter}
                  label="Ride Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                  startAdornment={<FilterListIcon fontSize="small" sx={{ mr: 1, color: 'action.active' }} />}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="requested">Requested</MenuItem>
                <MenuItem value="accepted">Accepted</MenuItem>
                <MenuItem value="in_progress">In Progress</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>

            {/* Payment Filter */}
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Payment Status</InputLabel>
              <Select
                  value={paymentFilter}
                  label="Payment Status"
                  onChange={(e) => setPaymentFilter(e.target.value)}
              >
                <MenuItem value="all">All Payments</MenuItem>
                <MenuItem value="paid">Paid</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
              </Select>
            </FormControl>

            {/* Clear Filters Button (only shows if filters active) */}
            {(searchTerm || statusFilter !== 'all' || paymentFilter !== 'all') && (
                <Button
                    variant="text"
                    size="small"
                    onClick={() => {
                      setSearchTerm("");
                      setStatusFilter("all");
                      setPaymentFilter("all");
                    }}
                >
                  Clear
                </Button>
            )}
          </Box>
        </Toolbar>
        <StyledTableContainer sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
            ) : (
              <Table stickyHeader sx={{ minWidth: 1200 }}>
                <TableHead>
                  <StyledTableRow>
                    {columns.map(col => (
                        <StyledTableCell key={col.accessor}>{col.label}</StyledTableCell>
                    ))}
                  </StyledTableRow>
                </TableHead>
                <TableBody>
                  {filteredItems.map(ride => (
                      <StyledTableRow
                          hover
                          sx={{ cursor: "pointer" }}
                          key={ride.id}
                          onClick={() => handleRowClick(ride.id)}
                          onContextMenu={(e) => handleContextMenu(e, ride.id)}
                      >
                        {/* Ride ID */}
                        <StyledTableCell>
                          <Tooltip title={ride.id}>
                            <span style={{ maxWidth: 90, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "inline-block" }}>
                              {ride.id}
                            </span>
                          </Tooltip>
                        </StyledTableCell>
                        {/* Rider ID */}
                        <StyledTableCell>
                          <Tooltip title={ride?.rider?.phone}>
                            <span style={{ maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "inline-block" }}>
                              {ride?.rider?.phone}
                            </span>
                          </Tooltip>
                        </StyledTableCell>
                        {/* Driver ID */}
                        <StyledTableCell>
                          <Tooltip title={ride.driver?.phone}>
                            <span style={{ maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "inline-block" }}>
                              {ride.driver?.phone}
                            </span>
                          </Tooltip>
                        </StyledTableCell>
                        {/* Pickup Location */}
                        <StyledTableCell>
                          <Tooltip title={ride.pickupLocation}>
                            <span style={{ maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "inline-block" }}>
                              {ride.pickupLocation}
                            </span>
                          </Tooltip>
                        </StyledTableCell>
                        {/* Dropoff Location */}
                        <StyledTableCell>
                          <Tooltip title={ride.dropoffLocation}>
                            <span style={{ maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "inline-block" }}>
                              {ride.dropoffLocation}
                            </span>
                          </Tooltip>
                        </StyledTableCell>
                        <StyledTableCell>
                          <Chip
                              label={ride.status}
                              color={statusColor(ride.status)}
                              variant="filled"
                              size="small"
                          />
                        </StyledTableCell>
                        <StyledTableCell>
                          <Chip
                              label={ride.paymentStatus || "-"}
                              color={paymentStatusColor(ride.paymentStatus || '')}
                              variant="filled"
                              size="small"
                          />
                        </StyledTableCell>
                        <StyledTableCell>{ride.type || "-"}</StyledTableCell>
                        <StyledTableCell>₹{ride.fare ?? "-"}</StyledTableCell>
                        <StyledTableCell>{formatDate(ride.requestedAt)}</StyledTableCell>
                        <StyledTableCell>{formatDate(ride.matchedAt)}</StyledTableCell>
                        <StyledTableCell>{formatDate(ride.startedAt)}</StyledTableCell>
                        <StyledTableCell>{formatDate(ride.completedAt)}</StyledTableCell>
                        <StyledTableCell>{formatDate(ride.cancelledAt)}</StyledTableCell>
                        <StyledTableCell>
                          {ride.cancelledBy
                            ? <Chip
                                size="small"
                                label={ride.cancelledBy}
                                color={ride.cancelledBy === 'rider' ? 'info' : ride.cancelledBy === 'driver' ? 'warning' : 'default'}
                                sx={{ textTransform: 'capitalize' }}
                              />
                            : (['cancelled', 'expired'].includes(String(ride.status)) ? '—' : '')}
                        </StyledTableCell>
                        <StyledTableCell>
                          <Tooltip title={ride.cancellationReason || '-'}>
                            <span style={{ maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "inline-block" }}>
                              {ride.cancellationReason || '-'}
                            </span>
                          </Tooltip>
                        </StyledTableCell>
                        <StyledTableCell>{ride.distanceMeters ?? "-"}</StyledTableCell>
                        <StyledTableCell>{ride.durationSeconds ?? "-"}</StyledTableCell>
                        <StyledTableCell>{ride.tip ?? "-"}</StyledTableCell>
                        <StyledTableCell style={{ color: ride.riderSubsidyApplied ? 'red' : 'inherit' }}>
                          {ride.riderSubsidyApplied ? `-₹${ride.riderSubsidyApplied}` : "-"}
                        </StyledTableCell>
                        <StyledTableCell>₹{ride.totalFare ?? "-"}</StyledTableCell>
                      </StyledTableRow>
                  ))}
                  {filteredItems.length === 0 && !loading && (
                      <StyledTableRow>
                        <StyledTableCell colSpan={columns.length} align="center">
                          <Typography color="textSecondary" sx={{ py: 3 }}>
                            No rides found matching filters.
                          </Typography>
                        </StyledTableCell>
                      </StyledTableRow>
                  )}
                </TableBody>
              </Table>
            )}
        </StyledTableContainer>
        <LoadMoreBar
            count={filteredItems.length}
            hasMore={ridesList.hasMore}
            loading={ridesList.isLoadingMore}
            onLoadMore={ridesList.loadMore}
        />
        {error && <div style={{ color: "red", margin: 12 }}>{error}</div>}
        {/* Context Menu for Right Click */}
        <FancyContextMenu
            actions={menuActions}
            open={Boolean(contextMenu)}
            onClose={handleCloseContextMenu}
            anchorPosition={contextMenu}
            title={menuRideId ? `Ride #${menuRideId}` : "Ride Actions"}
        />

        {/* Ride Details Dialog (Quick View) */}
        <Dialog open={detailOpen} onClose={handleCloseDetail} fullWidth maxWidth="md">
          <DialogTitle>Ride Quick View</DialogTitle>
          <DialogContent>
            {selectedRide ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                  <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 2 }}>
                    {Object.keys(selectedRide).map((field) => (
                        <TextField
                            key={field}
                            label={field.charAt(0).toUpperCase() + field.slice(1)}
                            value={
                              ['requestedAt', 'matchedAt', 'startedAt', 'completedAt', 'cancelledAt', 'expiredAt'].includes(field)
                                  ? formatDate((selectedRide as any)[field])
                                  : (typeof (selectedRide as any)[field] === 'object' && (selectedRide as any)[field] !== null
                                  ? JSON.stringify((selectedRide as any)[field], null, 2)
                                  : String((selectedRide as any)[field] ?? "-"))
                            }
                            fullWidth
                            margin="dense"
                            InputLabelProps={{ shrink: true }}
                            InputProps={{ readOnly: true }}
                            multiline={typeof (selectedRide as any)[field] === 'object'}
                        />
                    ))}
                  </Box>
                  <Button variant="contained" color="secondary" onClick={() => navigate(`/ride/${selectedRide.id}`)}>
                    Go to Full Details Page
                  </Button>
                </Box>
            ) : <CircularProgress />}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDetail}>Close</Button>
          </DialogActions>
        </Dialog>
      </StyledPaper>
  );
};

export default RideList;